import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import img1 from "../schoolgirl.jpg";
const CSS_ZIndex = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎯 CSS Z-Index</h1>

      <p className="tutorial-intro">
        The <code>z-index</code> property in CSS controls the{" "}
        <strong>stacking order</strong> of elements. Elements with a higher{" "}
        <code>z-index</code> value appear in front of elements with a lower one.
        It only works on elements that have a positioning property (
        <code>relative</code>, <code>absolute</code>, <code>fixed</code>, or{" "}
        <code>sticky</code>).
      </p>

      {/* Why Use */}
      <div
        style={{
          background: "linear-gradient(135deg, #fbcfe8, #f9a8d4)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#9d174d", marginBottom: "0.5rem" }}>
          🌟 Why Use z-index?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Use <code>z-index</code> when elements overlap and you want to control
          which one appears on top. Commonly used for menus, modals, tooltips,
          and layered designs.
        </p>
      </div>

      {/* Basic Example */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Basic Example</h2>
        <p>
          Here, the red box has a higher <code>z-index</code>, so it appears on
          top of the blue box.
        </p>

        <CodeCopyBlock
          code={`.blue-box {
  position: relative;
  z-index: 1;
  background: lightblue;
}

.red-box {
  position: relative;
  z-index: 2;
  background: lightcoral;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            position: "relative",
            height: "120px",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "30px",
              top: "20px",
              width: "150px",
              height: "100px",
              background: "lightblue",
              zIndex: 1,
            }}
          >
            Blue Box (z-index: 1)
          </div>
          <div
            style={{
              position: "absolute",
              left: "80px",
              top: "50px",
              width: "150px",
              height: "100px",
              background: "lightcoral",
              zIndex: 2,
            }}
          >
            Red Box (z-index: 2)
          </div>
        </div>
      </div>

      {/* Negative z-index */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Negative z-index</h2>
        <p>
          You can use a negative <code>z-index</code> to push elements behind
          others. Be careful, as they may go behind the page background.
        </p>

        <CodeCopyBlock
          code={`.behind-box {
  position: relative;
  z-index: -1;
  background: lightgreen;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            position: "relative",
            height: "100px",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50px",
              top: "20px",
              width: "200px",
              height: "80px",
              background: "lightgreen",
              zIndex: -1,
            }}
          >
            Behind Box (z-index: -1)
          </div>
          <div
            style={{
              position: "absolute",
              left: "90px",
              top: "40px",
              width: "200px",
              height: "80px",
              background: "lightsalmon",
              zIndex: 1,
            }}
          >
            Front Box (z-index: 1)
          </div>
        </div>
      </div>

      {/* Practical Use */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Practical Uses</h2>
        <ul className="tutorial-list">
          <li>📌 Dropdown menus stay above other content.</li>
          <li>📌 Modal popups overlay the background.</li>
          <li>📌 Tooltips appear on top of text and buttons.</li>
          <li>📌 Sticky headers remain visible when scrolling.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you understand <code>z-index</code>, explore how it interacts
          with{" "}
          <a href="/css/position" className="learn-more">
            CSS Position
          </a>{" "}
          and stacking contexts for more advanced layouts.
        </p>
      </div>

      {/* Example 1: Stacked Images */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Example 1: Stacked Images</h2>
        <p>
          Two images overlap — the one with higher <code>z-index</code> appears
          on top.
        </p>

        <CodeCopyBlock
          code={`.img1 {
  position: absolute;
  z-index: 1;
}

.img2 {
  position: absolute;
  z-index: 2;
}`}
        />

        <div
          style={{
            position: "relative",
            height: "220px",
            marginTop: "1rem",
          }}
        >
          <img
            src={img1}
            alt="Image 1"
            style={{
              position: "absolute",
              top: "20px",
              left: "30px",
              zIndex: 1,
              borderRadius: "8px",
            }}
          />
          <img
            src={img1}
            alt="Image 2"
            style={{
              position: "absolute",
              top: "60px",
              left: "90px",
              zIndex: 2,
              borderRadius: "8px",
            }}
          />
        </div>
      </div>

      {/* Example 2: Image with Caption Overlay */}
      <div className="tutorial-section">
        <h2 className="section-title">
          🔹 Example 2: Image with Caption Overlay
        </h2>
        <p>
          Here, a caption box is placed above the image using{" "}
          <code>z-index</code>.
        </p>

        <CodeCopyBlock
          code={`.image-container {
  position: relative;
}

.caption {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0,0,0,0.7);
  color: white;
  padding: 5px;
  z-index: 2;
}`}
        />

        <div
          style={{
            position: "relative",
            display: "inline-block",
            marginTop: "1rem",
          }}
        >
          <img
            src={img1}
            alt="Nature"
            style={{
              display: "block",
              borderRadius: "8px",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "10px",
              background: "rgba(0,0,0,0.7)",
              color: "#fff",
              padding: "6px 12px",
              borderRadius: "4px",
              zIndex: 2,
            }}
          >
            🌿 Beautiful Nature
          </div>
        </div>
      </div>

      {/* Example 3: Modal Over Background */}
      <div className="tutorial-section">
        <h2 className="section-title">
          🔹 Example 3: Modal on Top of Background
        </h2>
        <p>
          A modal window uses a high <code>z-index</code> to stay above the
          background.
        </p>

        <CodeCopyBlock
          code={`.background {
  position: relative;
  z-index: 1;
}

.modal {
  position: absolute;
  z-index: 999;
  background: white;
  padding: 20px;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            position: "relative",
            padding: "10px",
          }}
        >
          <img
            src={img1}
            alt="Background"
            style={{
              width: "100%",
              borderRadius: "8px",
              zIndex: 1,
              position: "relative",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "40px",
              left: "60px",
              width: "200px",
              background: "white",
              padding: "15px",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
              zIndex: 999,
              textAlign: "center",
            }}
          >
            <h3 style={{ margin: "0 0 10px" }}>🔔 Modal</h3>
            <p style={{ fontSize: "14px" }}>
              This modal is above the background image.
            </p>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know how to use <code>z-index</code> with boxes and
          images, explore{" "}
          <a href="/css/position" className="learn-more">
            CSS Position
          </a>{" "}
          and stacking contexts to design complex layered layouts.
        </p>
      </div>
    </div>
  );
};

export default CSS_ZIndex;
